import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { BillingInfo } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  formatDate,
  useToast,
} from '../../components/ui.js';

function getSafeBillingUrl(value: string): URL | null {
  try {
    const url = new URL(value);
    const isAllowedHost =
      url.protocol === 'https:' &&
      (url.hostname === 'stripe.com' || url.hostname.endsWith('.stripe.com')) &&
      (url.port === '' || url.port === '443') &&
      !url.username &&
      !url.password;
    return isAllowedHost ? url : null;
  } catch {
    return null;
  }
}

export function BillingPage() {
  const { org } = useOrg();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const isAdmin = org.role === 'admin';

  const { data, isLoading } = useQuery({
    queryKey: ['billing', org.orgId],
    queryFn: () => apiFetch<BillingInfo>(`/orgs/${org.orgId}/billing`),
    enabled: isAdmin,
  });

  useEffect(() => {
    const status = params.get('status');
    if (status === 'success') {
      toast('Payment complete — your plan will update within a few moments.', 'success');
      setParams({}, { replace: true });
    } else if (status === 'cancelled') {
      toast('Checkout cancelled.', 'default');
      setParams({}, { replace: true });
    }
  }, [params, setParams, toast]);

  const goTo = (path: 'checkout' | 'portal') =>
    apiFetch<{ url: string }>(`/orgs/${org.orgId}/billing/${path}`, { method: 'POST' });

  const checkout = useMutation({
    mutationFn: () => goTo('checkout'),
    onSuccess: ({ url }) => {
      const safeUrl = getSafeBillingUrl(url);
      if (safeUrl) window.location.assign(safeUrl.href);
      else toast('Could not start checkout', 'error');
    },
    onError: (err) =>
      toast(
        err instanceof ApiRequestError && err.code === 'not_configured'
          ? 'Billing is not configured on this environment.'
          : err instanceof ApiRequestError
            ? err.message
            : 'Could not start checkout',
        'error',
      ),
  });
  const portal = useMutation({
    mutationFn: () => goTo('portal'),
    onSuccess: ({ url }) => {
      const safeUrl = getSafeBillingUrl(url);
      if (safeUrl) window.location.assign(safeUrl.href);
      else toast('Could not open the billing portal', 'error');
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not open the billing portal', 'error'),
  });

  if (!isAdmin) {
    return <EmptyState title="Admins only" message="Billing is managed by organisation admins." />;
  }
  if (isLoading || !data) return <LoadingPane label="Loading billing" />;

  return (
    <div>
      <PageHeader title="Billing" subtitle="Plan, seats and subscription status" />

      <Card title="Current plan">
        <p style={{ fontSize: 'var(--text-xl)', fontWeight: 700, marginBottom: 4 }}>
          {data.plan === 'paid' ? 'Team plan' : 'Free plan'}{' '}
          <Badge tone={data.plan === 'paid' ? 'success' : 'neutral'}>{data.subscriptionStatus}</Badge>
        </p>
        <p style={{ color: 'var(--color-text-muted)' }}>
          {data.plan === 'paid' ? (
            <>
              {data.memberCount} member(s) · renews {formatDate(data.currentPeriodEnd)} · billed
              annually
            </>
          ) : (
            <>
              The free plan includes a single user with full access. Upgrade to invite your team,
              assign reviewers and share the workload.
            </>
          )}
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          {data.plan === 'free' ? (
            <Button loading={checkout.isPending} onClick={() => checkout.mutate()}>
              Upgrade to Team
            </Button>
          ) : (
            <Button variant="secondary" loading={portal.isPending} onClick={() => portal.mutate()}>
              Manage subscription
            </Button>
          )}
        </div>
        {!data.stripeConfigured && (
          <p style={{ color: 'var(--color-warning)', marginTop: 12 }}>
            Stripe is not configured on this environment — upgrades are unavailable here.
          </p>
        )}
      </Card>

      <Card title="What the Team plan includes">
        <ul style={{ paddingLeft: 20, color: 'var(--color-text-muted)' }}>
          <li>Unlimited invitations for contributors, reviewers and admins</li>
          <li>External assessor access across your assessments</li>
          <li>All supported standards, exports and the API</li>
          <li>AI-assisted drafting (subject to platform configuration)</li>
        </ul>
      </Card>
    </div>
  );
}
